/** Formatting helpers shared across the console. */

const RELATIVE = new Intl.RelativeTimeFormat('en', { numeric: 'auto' })

const UNITS: [Intl.RelativeTimeFormatUnit, number][] = [
  ['year', 31_536_000],
  ['month', 2_592_000],
  ['week', 604_800],
  ['day', 86_400],
  ['hour', 3_600],
  ['minute', 60],
  ['second', 1],
]

export function relativeTime(value: string | Date | null | undefined): string {
  if (!value) return '—'
  const date = typeof value === 'string' ? new Date(value) : value
  if (Number.isNaN(date.getTime())) return '—'

  const seconds = (date.getTime() - Date.now()) / 1000
  const absolute = Math.abs(seconds)

  if (absolute < 45) return 'just now'
  for (const [unit, unitSeconds] of UNITS) {
    if (absolute >= unitSeconds) {
      return RELATIVE.format(Math.round(seconds / unitSeconds), unit)
    }
  }
  return 'just now'
}

export function formatDate(
  value: string | Date | null | undefined,
  options: Intl.DateTimeFormatOptions = { dateStyle: 'medium', timeStyle: 'short' },
): string {
  if (!value) return '—'
  const date = typeof value === 'string' ? new Date(value) : value
  if (Number.isNaN(date.getTime())) return '—'
  return new Intl.DateTimeFormat('en-GB', options).format(date)
}

export function formatDay(value: string): string {
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return value
  return new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'short' }).format(date)
}

/** Compact durations: 45s, 12m, 3.2h, 4d. */
export function duration(seconds: number | null | undefined): string {
  if (seconds === null || seconds === undefined) return '—'
  if (seconds < 60) return `${Math.round(seconds)}s`
  if (seconds < 3_600) return `${Math.round(seconds / 60)}m`
  if (seconds < 86_400) {
    const hours = seconds / 3_600
    return `${hours < 10 ? hours.toFixed(1) : Math.round(hours)}h`
  }
  const days = seconds / 86_400
  return `${days < 10 ? days.toFixed(1) : Math.round(days)}d`
}

/** Time spent in voice: 45m, 3h, 114h, 1.2k h.
 *
 *  Hours rather than `duration`'s days, because "4.8d in voice" reads as four
 *  days away from the server rather than a hundred and fifteen hours in it.
 *  Nobody counts their evenings in days. */
export function voiceTime(seconds: number | null | undefined): string {
  if (seconds === null || seconds === undefined) return '—'
  if (seconds < 60) return seconds > 0 ? '<1m' : '—'
  if (seconds < 3_600) return `${Math.round(seconds / 60)}m`
  const hours = Math.round(seconds / 3_600)
  return hours < 1000 ? `${hours}h` : `${compactNumber(hours)} h`
}

/** Spelled-out durations for settings copy: "2 hours", "30 minutes". */
export function longDuration(seconds: number | null | undefined): string {
  if (!seconds) return 'none'
  const units: [string, number][] = [
    ['day', 86_400],
    ['hour', 3_600],
    ['minute', 60],
    ['second', 1],
  ]
  for (const [name, size] of units) {
    if (seconds >= size) {
      const count = Math.round(seconds / size)
      return `${count} ${name}${count === 1 ? '' : 's'}`
    }
  }
  return `${seconds} seconds`
}

export function compactNumber(value: number | null | undefined): string {
  if (value === null || value === undefined) return '—'
  if (Math.abs(value) < 1000) return String(Math.round(value))
  return new Intl.NumberFormat('en', { notation: 'compact', maximumFractionDigits: 1 }).format(
    value,
  )
}

export function fullNumber(value: number | null | undefined): string {
  if (value === null || value === undefined) return '—'
  return new Intl.NumberFormat('en').format(value)
}

export function percent(value: number | null | undefined, digits = 0): string {
  if (value === null || value === undefined) return '—'
  return `${value.toFixed(digits)}%`
}

/** Discord role colours arrive as 24-bit ints; 0 means "no colour set". */
export function roleColour(value: number): string | null {
  if (!value) return null
  return `#${value.toString(16).padStart(6, '0')}`
}

export function initials(name: string): string {
  const parts = name
    .trim()
    .split(/[\s_-]+/)
    .filter(Boolean)
  if (parts.length === 0) return '?'
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase()
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
}

export function truncate(value: string, length: number): string {
  return value.length <= length ? value : `${value.slice(0, length - 1)}…`
}

/** Turn "ticket_category.create" into "Ticket category created"-ish prose. */
export function humanise(value: string): string {
  return value
    .replace(/[._]/g, ' ')
    .replace(/\b\w/g, (character) => character.toUpperCase())
    .trim()
}
